<script setup>
import { computed, ref, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { DirectUpload } from 'activestorage';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['sendMessage', 'back']);

const { t } = useI18n();

const processedParams = ref({});

// Media state
const mediaInputType = ref('url'); // url | upload | id
const mediaUrl = ref('');
const mediaId = ref('');
const mediaFile = ref(null);
const mediaPreviewUrl = ref('');
const isUploading = ref(false);

const templateName = computed(() => {
  return props.template?.name || '';
});

const templateString = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'BODY'
  ).text;
});

// Detect header with media
const headerComponent = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'HEADER'
  );
});

// Detect footer
const footerComponent = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'FOOTER'
  );
});

// Detect buttons
const buttonsComponent = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'BUTTONS'
  );
});

const headerFormat = computed(() => {
  return headerComponent.value?.format || null;
});

const hasMediaHeader = computed(() => {
  return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat.value);
});

const mediaTypeLabel = computed(() => {
  const labels = {
    IMAGE: 'Imagem',
    VIDEO: 'Vídeo',
    DOCUMENT: 'Documento',
  };
  return labels[headerFormat.value] || '';
});

const mediaIcon = computed(() => {
  const icons = {
    IMAGE: 'i-lucide-image',
    VIDEO: 'i-lucide-video',
    DOCUMENT: 'i-lucide-file-text',
  };
  return icons[headerFormat.value] || 'i-lucide-file';
});

const acceptedFileTypes = computed(() => {
  const types = {
    IMAGE: 'image/jpeg,image/png,image/webp',
    VIDEO: 'video/mp4,video/3gpp',
    DOCUMENT:
      'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return types[headerFormat.value] || '*/*';
});

const isValidHttpUrl = url => /^https?:\/\//i.test(url || '');

const currentMediaValue = computed(() => {
  if (mediaInputType.value === 'url') {
    return mediaUrl.value;
  }
  if (mediaInputType.value === 'id') {
    return mediaId.value;
  }
  return mediaPreviewUrl.value;
});

const isMediaValid = computed(() => {
  if (!hasMediaHeader.value) return true;
  const value = currentMediaValue.value?.trim();
  if (!value) return false;
  if (mediaInputType.value === 'id') return true;
  return isValidHttpUrl(value);
});

const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const processedString = computed(() => {
  return templateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.value[variableKey] || `{{${variable}}}`;
  });
});

const processedStringWithVariableHighlight = computed(() => {
  const variables = templateString.value.match(/{{([^}]+)}}/g) || [];

  return variables.reduce((result, variable) => {
    const variableKey = processVariable(variable);
    const value = processedParams.value[variableKey] || variable;
    return result.replace(
      variable,
      `<span class="break-all text-n-slate-12">${value}</span>`
    );
  }, templateString.value);
});

const rules = computed(() => {
  const paramRules = {};
  Object.keys(processedParams.value).forEach(key => {
    paramRules[key] = { required: requiredIf(true) };
  });
  return {
    processedParams: paramRules,
  };
});

const v$ = useVuelidate(rules, { processedParams });

const getFieldErrorType = key => {
  if (!v$.value.processedParams[key]?.$error) return 'info';
  return 'error';
};

const generateVariables = () => {
  const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
  if (!matchedVariables) return;

  const finalVars = matchedVariables.map(i => processVariable(i));
  processedParams.value = finalVars.reduce((acc, variable) => {
    acc[variable] = '';
    return acc;
  }, {});
};

const isDraggingOver = ref(false);

const processSelectedFile = file => {
  if (!file) return;

  mediaInputType.value = 'upload';

  const maxSize = 20 * 1024 * 1024;
  if (file.size > maxSize) {
    useAlert('O arquivo deve ter no máximo 20MB');
    return;
  }

  mediaFile.value = file;

  if (headerFormat.value === 'IMAGE' || headerFormat.value === 'VIDEO') {
    mediaPreviewUrl.value = URL.createObjectURL(file);
  }

  uploadFile(file);
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  processSelectedFile(file);
};

const handleDrop = event => {
  const file = event.dataTransfer?.files?.[0];
  isDraggingOver.value = false;
  processSelectedFile(file);
};

const handleDragOver = event => {
  event.preventDefault();
  isDraggingOver.value = true;
};

const handleDragLeave = event => {
  if (
    event.currentTarget &&
    event.relatedTarget &&
    event.currentTarget.contains(event.relatedTarget)
  ) {
    return;
  }
  isDraggingOver.value = false;
};

const uploadFile = async file => {
  isUploading.value = true;

  try {
    const upload = new DirectUpload(
      file,
      '/rails/active_storage/direct_uploads'
    );

    upload.create((error, blob) => {
      isUploading.value = false;

      if (error) {
        useAlert('Erro ao fazer upload do arquivo');
        console.error('Upload error:', error);
        return;
      }

      const publicUrl = `/rails/active_storage/blobs/${blob.signed_id}/${blob.filename}`;
      mediaPreviewUrl.value = window.location.origin + publicUrl;
    });
  } catch (error) {
    isUploading.value = false;
    useAlert('Erro ao fazer upload do arquivo');
    console.error('Upload error:', error);
  }
};

const clearMedia = () => {
  mediaFile.value = null;
  mediaUrl.value = '';
  mediaPreviewUrl.value = '';
};

const sendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  if (isUploading.value) {
    useAlert('Aguarde o upload do arquivo terminar antes de enviar o template');
    return;
  }

  if (hasMediaHeader.value && !isMediaValid.value) {
    useAlert(
      `Forneça uma URL válida (http/https) ou ID para o header de ${mediaTypeLabel.value.toLowerCase()}`
    );
    return;
  }

  const bodyParams = processedParams.value || {};
  const enhancedParams = { body: bodyParams };

  if (hasMediaHeader.value && currentMediaValue.value) {
    enhancedParams.header = {
      media_type: headerFormat.value.toLowerCase(),
    };

    if (mediaInputType.value === 'id') {
      enhancedParams.header.media_id = currentMediaValue.value.trim();
    } else {
      enhancedParams.header.media_url = currentMediaValue.value.trim();
    }

    if (headerFormat.value === 'DOCUMENT' && mediaFile.value?.name) {
      enhancedParams.header.media_name = mediaFile.value.name;
    }
  }

  const payload = {
    message: processedString.value,
    templateParams: {
      name: props.template.name,
      category: props.template.category,
      language: props.template.language,
      namespace: props.template.namespace,
      processed_params: enhancedParams,
      footer: footerComponent.value?.text || '',
      buttons: buttonsComponent.value?.buttons || [],
    },
  };

  emit('sendMessage', payload);
};

onMounted(() => {
  generateVariables();
});
</script>

<template>
  <div
    class="absolute top-full mt-1.5 max-h-[500px] overflow-y-auto left-0 flex flex-col gap-4 px-4 pt-6 pb-5 items-start w-[460px] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
    @dragover.prevent
    @dragenter.prevent
    @drop.prevent
  >
    <!-- Media Header Section -->
    <div v-if="hasMediaHeader" class="w-full">
      <div class="flex items-center gap-2 mb-3">
        <i :class="mediaIcon" class="text-n-iris-9" />
        <span class="text-sm font-medium text-n-slate-12">
          Header de {{ mediaTypeLabel }} (obrigatório)
        </span>
      </div>

      <!-- Input Type Toggle -->
      <div class="flex gap-2 mb-3">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors"
          :class="
            mediaInputType === 'upload'
              ? 'bg-n-iris-2 border-n-iris-6 text-n-iris-11'
              : 'bg-transparent border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="mediaInputType = 'upload'"
        >
          <i class="i-lucide-upload text-sm" />
          Upload
        </button>
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors"
          :class="
            mediaInputType === 'url'
              ? 'bg-n-iris-2 border-n-iris-6 text-n-iris-11'
              : 'bg-transparent border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="mediaInputType = 'url'"
      >
        <i class="i-lucide-link text-sm" />
        URL
      </button>
    </div>

    <!-- URL Input -->
    <div v-if="mediaInputType === 'url'" class="w-full">
      <input
          v-model="mediaUrl"
          type="url"
          class="w-full px-3 py-2 text-sm bg-n-alpha-2 dark:bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-iris-6"
        placeholder="https://exemplo.com/imagem.jpg"
      />
    </div>

    <!-- Upload Input -->
    <div v-else class="w-full">
      <label
        class="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
        :class="[
          isUploading
            ? 'border-n-iris-6 bg-n-iris-2'
            : 'border-n-slate-6 hover:border-n-iris-6 hover:bg-n-iris-2',
          isDraggingOver && !isUploading ? 'border-n-iris-6 bg-n-iris-2/70' : ''
        ]"
        @dragover.stop="handleDragOver"
        @dragenter.stop="handleDragOver"
        @dragleave.stop="handleDragLeave"
        @drop.prevent.stop="handleDrop"
      >
          <input
            type="file"
            class="hidden"
            :accept="acceptedFileTypes"
            @change="handleFileSelect"
          />
          <template v-if="isUploading">
            <div
              class="w-5 h-5 border-2 border-n-slate-4 border-t-n-iris-9 rounded-full animate-spin"
            />
            <span class="text-xs text-n-slate-11">Enviando...</span>
          </template>
          <template v-else-if="mediaFile">
            <i class="i-lucide-check-circle text-n-teal-9 text-lg" />
            <span class="text-xs text-n-slate-12 text-center truncate max-w-full">
              {{ mediaFile.name }}
            </span>
            <button
              type="button"
              class="text-xs text-n-ruby-11 hover:underline"
              @click.prevent="clearMedia"
            >
              Remover
            </button>
          </template>
          <template v-else>
            <i class="i-lucide-upload text-n-slate-9 text-lg" />
            <span class="text-xs text-n-slate-11">
              Clique para selecionar {{ mediaTypeLabel.toLowerCase() }}
            </span>
            <span class="text-xs text-n-slate-10">Máximo 20MB</span>
          </template>
        </label>
      </div>

      <!-- Preview -->
    <div
      v-if="currentMediaValue && headerFormat === 'IMAGE' && mediaInputType !== 'id'"
      class="mt-3 rounded-lg overflow-hidden border border-n-weak"
    >
      <img
        :src="currentMediaValue"
        alt="Preview"
        class="w-full max-h-32 object-contain bg-n-alpha-2"
      />
    </div>
    <div
      v-else-if="currentMediaValue && headerFormat === 'VIDEO' && mediaInputType !== 'id'"
      class="mt-3 rounded-lg overflow-hidden border border-n-weak"
    >
      <video :src="currentMediaValue" controls class="w-full max-h-32" />
    </div>
    </div>

    <span class="text-sm text-n-slate-12">
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.TEMPLATE_NAME',
          { templateName: templateName }
        )
      }}
    </span>
    <p
      class="mb-0 text-sm text-n-slate-11"
      v-html="processedStringWithVariableHighlight"
    />

    <span
      v-if="Object.keys(processedParams).length"
      class="text-sm font-medium text-n-slate-12"
    >
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.VARIABLES'
        )
      }}
    </span>

    <div
      v-for="(variable, key) in processedParams"
      :key="key"
      class="flex items-center w-full gap-2"
    >
      <span
        class="flex items-center h-8 text-sm min-w-6 ltr:text-left rtl:text-right text-n-slate-10"
      >
        {{ key }}
      </span>
      <Input
        v-model="processedParams[key]"
        custom-input-class="!h-8 w-full !bg-transparent"
        class="w-full"
        :message-type="getFieldErrorType(key)"
      />
    </div>

    <div class="flex items-end justify-between w-full gap-3 h-14">
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.BACK'
          )
        "
        color="slate"
        variant="faded"
        class="w-full font-medium"
        @click="emit('back')"
      />
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.SEND_MESSAGE'
          )
        "
        class="w-full font-medium"
        @click="sendMessage"
      />
    </div>
  </div>
</template>
